import {TableType as BookingTableType} from "../../containers/bookings/BookingList/BookingListColumns";
import {BookingType, PaymentProvider} from "../../generated/graphql";
import {mollieMethodLabel} from "../mollieMethodLabels";
import {csvArray, csvBirthday, csvBirthdayAsAge, csvBookingType, csvBoolean, csvDate, csvInvoice, csvMoney, csvPayDate, csvPaymentMethod, csvPaymentType} from "./TableColumnExports";
import {CurrencyShort} from "../renderAmount";
import {formatPersonName} from "../formatPersonName";
import {isBookingSettled} from "../bookingDetailsRouting";

export const bookingValues = (
    key: string,
    row: BookingTableType,
    index: number,
    currencyShort: CurrencyShort,
    notExportable: string,
    outputFormat?: "pdf" | "csv",
    // eslint-disable-next-line max-params
): string | number => {
    // PERFORMANCE OPTIMIZATION: Verwende row.course direkt statt row.courseLessons[0].course
    // Die optimierte Query liefert alle Kurs-Daten in row.course

    switch (key) {
        case "rowIndex":
            return `${index + 1}`;
        case "bookingNumber":
            return `${row.bookingNumberPrefix}-${row.bookingNumber}`;
        case "bookingNumberPrefix":
            return `${row.bookingNumberPrefix}`;
        case "bookingType":
            return csvBookingType(row.bookingType as BookingType);
        case "updateDate":
            return csvDate(row.updateDate);
        case "noticeStaff":
            return row.noticeStaff ?? "";
        case "noticeBooker":
            return row.noticeBooker ?? "";
        case "foundUs":
            return `${row.foundUs ?? "-"}`;
        case "utmSource":
            // Vor der Server-Prüfung gespeicherte Werte können mit =, +, -, @ beginnen: als Text exportieren, nicht als Formel.
            return /^[=+\-@\t\r]/.test(row.utmSource ?? "") ? `'${row.utmSource}` : row.utmSource ?? "";
        case "coursePriceId":
            return row.coursePriceId ?? "";
        case "payDate":
            return csvPayDate(row.payDate);
        case "coursePrice":
            return `${row.coursePrice.grossPrice}`;
        case "invoice":
            return csvInvoice(row.invoice);
        case "paymentType":
            return csvPaymentType(row.paymentType);
        case "courseLessons":
            return `${row.course?.lessonCount ?? row.courseLessons?.length ?? 0}`;
        case "bookedBy":
            return csvBoolean(Boolean(row.bookedBy === "Booker"));
        case "courseType":
            return `${row.course?.courseType?.name ?? "-"}`;
        case "prefixedCourseNumber":
            return `${row.course?.prefixedCourseNumber ?? "-"}`;
        case "bookerFullName":
            return `${row.attendee?.booker?.firstname} ${row.attendee?.booker?.lastname}`;
        case "bookerLastFirst":
            return formatPersonName(row.attendee?.booker?.firstname, row.attendee?.booker?.lastname, true);
        case "phoneNumber":
            return row.attendee?.booker?.phoneNumber ?? "";
        case "mobileNumber":
            return row.attendee?.booker?.mobileNumber ?? "";
        case "email":
            return row.attendee?.booker?.email ?? "";
        case "attendeeFullName":
            return `${row.attendee?.firstname} ${row.attendee?.lastname}`;
        case "attendeeLastFirst":
            return formatPersonName(row.attendee?.firstname, row.attendee?.lastname, true);
        case "city":
            return `${row.attendee?.booker?.city ?? "-"}`;
        case "location":
            return `${row.course?.location?.name ?? "-"}`;
        case "firstCourseLesson":
            return csvDate(row.course?.startDateTime, true);
        case "lastCourseLesson":
            return csvDate(row.course?.endDateTime, true);
        case "billed":
            return isBookingSettled(row) ? "Ja" : "Nein";
        case "grossPrice":
            return csvMoney(row.grossPrice, currencyShort, outputFormat);
        case "netPrice":
            return csvMoney(row.netPrice, currencyShort, outputFormat);
        case "vat":
            return `${row.vat}`;
        case "invoiceNumber":
            return row.invoice?.invoiceNumber != null ? String(row.invoice.invoiceNumber) : "";
        case "isSpecial":
            return row.isSpecial === true ? "Ja" : "Nein";
        case "emailNotifications":
            return notExportable;
        case "nonDisclosureNotice":
            return row.nonDisclosureNotice ?? "";
        case "birthday":
            return csvBirthday(row.attendee?.birthday);
        case "age":
            return csvBirthdayAsAge(row.attendee?.birthday);
        case "dateOfBooking":
            return csvDate(row.createDate);
        case "createDate":
            return csvDate(row.createDate, true);
        case "canceled":
            return csvDate(row.canceled);
        case "paymentMethod": {
            // Wie die Listenspalte: Bei Mollie steht die Methode in den Raten (zuletzt erfasste), die Buchung hat keine.
            const mollieRate = [...(row.scheduledPayments ?? [])].reverse().find((sp) => sp.paymentProvider === PaymentProvider.Mollie && Boolean(sp.mollieMethod));

            if (mollieRate?.mollieMethod) {
                return mollieMethodLabel(mollieRate.mollieMethod);
            }

            // Zahlungsmittel, nicht Zahlungsart: csvPaymentType kennt die PaymentTransferType-Werte nicht und lieferte immer „-“.
            return csvPaymentMethod(row.paymentMethod);
        }
        case "discountCode":
            return row.discountCodeText ?? "";
        case "credit":
            return csvMoney(row.attendee?.booker?.credit ?? 0, currencyShort);
        case "instructors":
            return csvArray(row.course?.instructors?.map((instructor) => `${instructor.firstname} ${instructor.lastname}`) ?? []);
        case "lessonCount":
            return `${row.course?.lessonCount ?? row.courseLessons?.length ?? 0}`;
        default:
            return notExportable;
    }
};
